import { canv } from "@/canv/global";
import {
    drawRoundedRect,
    generateUID,
    getStringProperty,
    isOnScreenAfterTranslate,
    isOverRect,
} from "@/canv/utils";
import { checkIsJespoDumps, copyProccessorExec, pasteProccessor } from "@/canvasController/helpers";
import { ElementBase } from "@/elementBase";
import type { ElementBaseArea, SearchTextObject } from "@/elementBase/types";
import { MindMapWidgetInterface } from "@/elements/mindMap/interface";
import { MindMapWidgetNode } from "@/elements/mindMap/node";
import { Group } from "@/group";
import { generateKeyBetween } from "@/helpers/fractional-indexing";
import { selectionManager } from "@/tools/cursorTool";
import { queryAreaByGroup } from "@/utils";
import { widgetOption } from "@/widgetMenu/widgetMenu";
import { regenerateTextPartUID } from "../text/textPartFactory";
import type {
    Direction,
    FlyParentDirection,
    MindMapCalculateBranchBoxData,
    MindMapDrawData,
    MindMapFlyData,
    MindMapSelectionProps,
    MindMapTwinState,
    MindMapWidgetNodeFixState,
    MindMapWidgetNodeState,
} from "./types";

export class MindMapWidget extends ElementBase {
    bindNodeTextAutosave(node: MindMapWidgetNode) {
        node.textPart.setEditingAutosaveHandler(() => {
            canv.eventManager.openRecord();
            this.saveState("nodeFocus");
            canv.eventManager.closeRecord();
            this.fixState("nodeFocus");
        });
    }

    constructor(startX: number, startY: number) {
        super();

        this.interface = new MindMapWidgetInterface(this);

        const rootNode = new MindMapWidgetNode(startX, startY);

        rootNode.textPart.menuTextBold(true);
        rootNode.textPart.menuTextSize(41);
        rootNode.updateDims();

        this.nodes.push(rootNode);

        this.nothingWhenInteraction = true;

        this.alignMindMap(); // при создании виджета тут всегда одна нода
        this.updateDims();
    }

    updateDims() {
        const data = this.getSelectionProps(this.nodes);

        this.startX = data.startX;
        this.startY = data.startY;
        this.width = data.width;
        this.height = data.height;

        this.updateBase(this.startX, this.startY, this.width, this.height);
    }

    async TryAction(type: string) {
        this.interface.TryAction(type);

        if (type === "mousedown" && this.interface.isAction().status) {
            // тут не бывает nodeFocus && isOnlyEmptyLine(). Interface это проверяет. Но даже если будет - ок

            this.saveInteraction();

            const onlySelected = this.getOnlySelectedNode();
            const actionInfo = this.interface.isAction();

            if (onlySelected) {
                if (actionInfo.data?.type === "child") {
                    this.startNodeFocus(
                        this.addChild(onlySelected, actionInfo.data.direction as Direction),
                    );
                } else {
                    this.startNodeFocus(
                        this.addNeighbour(onlySelected, actionInfo.data?.side as string),
                    );
                }
            }

            canv.widgetMenu.reopen();
        } else if (this.interactionType === "fly") {
            if (type === "mousemove") {
                this.applyFly();
            } else if (type === "mouseup") {
                this.stopFly();
                this.updateDims(); // обновляем общие dimensions
                this.interactionType = "select";
                canv.widgetMenu.reopen(); // переоткрыаем меню, так как wm_link ориентируется на interactionType
            }
        } else if (this.interactionType === "nodeFocus") {
            const onlySelectedNode = this.getOnlySelectedNode();
            await onlySelectedNode?.TryAction(type);

            if (type === "keyboardDown") {
                this.tryShortcut();
                // нужно выровнить детей (они передвунуться по x для horizontal... + при много строчном тексте нужно выровнить все вверху (alignChildren рекурсивно все сделает))
                if (onlySelectedNode) {
                    this.alignChildrenAll(onlySelectedNode);
                }
                this.updateDims(); // обновляем общие dimensions
            } else if (type === "mousedown") {
                const onlySelectedNode = this.getOnlySelectedNode();

                if (
                    onlySelectedNode &&
                    !isOverRect(
                        onlySelectedNode.startX,
                        onlySelectedNode.startY,
                        onlySelectedNode.width,
                        onlySelectedNode.height,
                    )
                ) {
                    this.saveInteraction();
                    this.interactionType = "select";
                    canv.widgetMenu.reopen(); // переоткрыаем меню, так как wm_link ориентируется на interactionType

                    if (onlySelectedNode.textPart.isOnlyEmptyLine()) {
                        this.deleteSelectedNodes();
                        canv.toolBox.TryGetToolByName("cursorTool").TryAction("mousedown");
                    }
                }
            }
        } else if (this.interactionType === "select" || this.interactionType === "fastSelect") {
            if (type === "keyboardDown") {
                if (canv.keyboard.isBackspace() || canv.keyboard.isDelete()) {
                    this.deleteSelectedNodes();
                } else {
                    const onlySelectedNode = this.getOnlySelectedNode();

                    if (
                        onlySelectedNode &&
                        canv.keyboard.isSymbol &&
                        !canv.keyboard.isEnter() &&
                        this.selected.length === 1
                    ) {
                        this.startNodeClearTextAndFocus(onlySelectedNode);
                        await onlySelectedNode.TryAction(type);
                        this.alignChildrenAll(onlySelectedNode);
                        this.updateDims();
                    }
                    this.tryShortcut();
                }
            } else if (type === "mousedown") {
                if (canv.keyboard.isShift()) {
                    if (this.isOverAnyNode().status) {
                        this.toggleSelected();

                        if (this.selected.length === 0) {
                            this.stopInteraction();

                            canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                            // не делаем тут cursorTool.TryAction("mousedown"); так как иначе он может заново выбрать ноду, если мы сняли выделение с последней
                        }
                    } else {
                        this.stopInteraction();

                        canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                        canv.toolBox.TryGetToolByName("cursorTool").TryAction("mousedown");
                    }
                } else if (canv.keyboard.isAlt() && !this.isLocked) {
                    if (this.interactionType !== "select") return;

                    const onlySelectedNode = this.getOnlySelectedNode();
                    if (this.selected.length > 1 || onlySelectedNode?.parentNodeUID === "") {
                        canv.toolBox.TryGetToolByName("cursorTool").turnMode("copying");
                    } else if (onlySelectedNode?.parentNodeUID) {
                        // todo: доработать с реализацией дублирования в tryShortcut при canv.keyboard.isDuplicateCommand()
                    }
                } else {
                    if (!this.isOverAnySelected()) {
                        this.stopInteraction();

                        canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                        canv.toolBox.TryGetToolByName("cursorTool").TryAction("mousedown");
                    }
                }
            } else if (type === "mouseup") {
                if (this.interactionType === "fastSelect") {
                    this.interactionType = "select";
                } else if (
                    canv.actionManager.isClick() &&
                    !canv.actionManager.rightButtonClickedPrevValue
                ) {
                    const onlySelected = this.getOnlySelectedNode();

                    if (
                        this.selected.length === 1 &&
                        onlySelected &&
                        isOverRect(
                            onlySelected.startX,
                            onlySelected.startY,
                            onlySelected.width,
                            onlySelected.height,
                        )
                    ) {
                        this.startNodeFocus(onlySelected, true);
                    }
                }
            } else if (type === "mousemove" && !canv.actionManager.rightButtonClickedPrevValue) {
                if (
                    canv.actionManager.isMouseDown() &&
                    this.isOverAnySelected() &&
                    this.selected.length === 1
                ) {
                    // isOverAnySelected нужен при выходе из focus и сразу mousemove
                    this.startFly();
                    this.interactionType = "fly";
                }
            }
        } else if (this.interactionType === "selecting") {
            if (type === "mousemove") {
                this.updateSelecting();
            } else if (type === "mouseup") {
                if (this.selected.length === 0) {
                    this.stopInteraction();

                    canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                } else {
                    this.interactionType = "select";
                    canv.widgetMenu.reopen();
                }
            }
        }
    }

    isInteraction(context: string, force = false) {
        if (context === "" && force) {
            const rootNode = this.getRootNode();

            if (rootNode) {
                this.startNodeFocus(rootNode);
                rootNode?.textPart.selection.selectAll();
            }

            return { type: "interaction" };
        } else if (context === "click" && this.isOverAnyNode().status) {
            if (canv.keyboard.isAlt() && !this.isLocked) {
                const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");
                if (cursorTool) {
                    selectionManager.setSelected([this.parent]);
                    canv.widgetMenu.hide();
                    cursorTool.turnMode("copying");

                    return { type: "none" };
                }
            } else {
                const node = this.isOverAnyNode();

                if (node?.nodeUID) {
                    this.interactionType = "fastSelect";
                    this.selected = [node.nodeUID];
                }
            }

            return { type: "interaction" };
        } else if (context === "selecting") {
            const nodesUnderSelecting = this.isSelectingAction();
            if (nodesUnderSelecting.length !== 0) {
                this.interactionType = "selecting";
                this.selected = nodesUnderSelecting;
                return { type: "interaction" };
            }
        }

        return { type: "none" };
    }

    stopInteraction() {
        this.saveInteraction();

        this.interactionType = "";
        this.selected = [];
    }

    saveInteraction() {
        if (this.interactionType === "nodeFocus") {
            const node = this.getOnlySelectedNode();

            if (node) {
                node.textPart.unfocusText();

                const shouldSave = !node.textPart.consumeHostInteractionSaveSkip();

                if (shouldSave) {
                    canv.eventManager.openRecord();
                    this.saveState("nodeFocus");
                    canv.eventManager.closeRecord();
                }
            }
        }
    }

    updateAfterMobileEdit() {
        canv.eventManager.openRecord();
        this.fixState("full");
        this.getOnlySelectedNode()?.updateDims();
        this.alignMindMap();
        this.saveState("full");
        canv.eventManager.closeRecord();
    }

    startNodeFocus(node: MindMapWidgetNode, focusAtPointer = false) {
        // выбирать selected нужно до fixState, так как он внутри использует selected
        this.selected = [node.UID];
        this.interactionType = "nodeFocus";
        this.bindNodeTextAutosave(node);
        node.textPart.focusText(focusAtPointer);
        node.updateDims();
        this.alignChildrenAll(node);
        this.updateDims();
        this.fixState("nodeFocus");
        canv.widgetMenu.reopen();
    }

    startNodeClearTextAndFocus(node: MindMapWidgetNode) {
        this.selected = [node.UID];
        this.interactionType = "nodeFocus";
        this.bindNodeTextAutosave(node);
        node.textPart.focusText();
        node.textPart.selection.selectAll();
        node.textPart.processDelete();
        canv.widgetMenu.reopen();
    }

    pastTextIntoNode(node: MindMapWidgetNode) {
        checkIsJespoDumps().then((isJespoDump) => {
            if (isJespoDump) {
                pasteProccessor();
            } else {
                this.interactionType = "nodeFocus";
                this.startNodeClearTextAndFocus(node);
                this.TryAction("keyboardDown");
                canv.widgetMenu.reopen();
            }
        });
    }

    tryShortcut() {
        const onlySelectedNode = this.getOnlySelectedNode();

        if (!onlySelectedNode) return;

        if (canv.keyboard.isCutCommand()) {
            if (this.interactionType !== "select") return;
            this.copySelectedNodes();
            this.deleteSelectedNodes();
        } else if (canv.keyboard.isCopyCommand()) {
            if (this.interactionType !== "select") return;
            this.copySelectedNodes();
        } else if (canv.keyboard.isDuplicateCommand()) {
            if (this.interactionType !== "select") return;
            if (this.selected.length > 1) {
                // if selected more than 1 node - duplicate selected
                canv.canvasController.duplicateProccessor();
            } else if (onlySelectedNode?.parentNodeUID === "") {
                // if parent node selected - duplicate as new mindmap
                canv.canvasController.duplicateProccessor();
            } else if (onlySelectedNode?.parentNodeUID) {
                // if child node selected - duplicate at the same level
                // todo: duplicate at the same level
            }
        } else if (canv.keyboard.isPasteCommand()) {
            if (this.interactionType !== "select") return;

            if (this.selected.length === 1) {
                const onlySelectedNode = this.getOnlySelectedNode();

                if (onlySelectedNode) {
                    this.pastTextIntoNode(onlySelectedNode);
                    this.alignChildrenAll(onlySelectedNode);
                }

                this.updateDims();
                return;
            }

            pasteProccessor();
        } else if (canv.keyboard.isCommandEnter()) {
            if (this.selected.length !== 1) return;

            this.saveInteraction();

            let direction: Direction = "direct";
            if (onlySelectedNode && onlySelectedNode.direction !== "") {
                direction = onlySelectedNode.direction;
            }

            const newNode = this.addChild(onlySelectedNode, direction);
            this.startNodeFocus(newNode);
        } else if (canv.keyboard.isCommandShiftEnter()) {
            if (this.selected.length !== 1) return;

            if (onlySelectedNode?.parentNodeUID === "") return; // для root не делаем

            this.saveInteraction();

            const newNode = this.addNeighbour(onlySelectedNode, "after");
            this.startNodeFocus(newNode);
        } else if (canv.keyboard.isEscCommand()) {
            if (this.interactionType === "nodeFocus") {
                this.saveInteraction();
                this.interactionType = "select";

                if (onlySelectedNode?.textPart.isOnlyEmptyLine()) {
                    this.deleteSelectedNodes();
                }
                canv.widgetMenu.reopen(); // переоткрыаем меню, так как wm_link ориентируется на interactionType
            } else if (this.interactionType === "select") {
                selectionManager.clearSelected();
            }
        } else if (canv.keyboard.isArrow()) {
            if (this.selected.length !== 1) return;
            if (this.interactionType !== "select") return;

            const isHorizontalModeNeighbourJump =
                this.directionMode === "horizontal" &&
                (canv.keyboard.isUpArrow() || canv.keyboard.isDownArrow());
            const isVerticalModeNeighbourJump =
                this.directionMode === "vertical" &&
                (canv.keyboard.isLeftArrow() || canv.keyboard.isRightArrow());

            let isParentJump = false;
            if (onlySelectedNode?.parentNodeUID !== "") {
                if (this.directionMode === "horizontal") {
                    isParentJump =
                        (onlySelectedNode?.direction === "direct" && canv.keyboard.isLeftArrow()) ||
                        (onlySelectedNode?.direction === "reverse" && canv.keyboard.isRightArrow());
                } else {
                    isParentJump =
                        (onlySelectedNode?.direction === "direct" && canv.keyboard.isUpArrow()) ||
                        (onlySelectedNode?.direction === "reverse" && canv.keyboard.isDownArrow());
                }
            }

            let isChildJump = false;
            let childJumpDirection = "direct";

            if (onlySelectedNode?.parentNodeUID === "") {
                if (this.directionMode === "horizontal") {
                    if (canv.keyboard.isLeftArrow() || canv.keyboard.isRightArrow()) {
                        isChildJump = true;
                        if (canv.keyboard.isRightArrow()) {
                            childJumpDirection = "direct";
                        } else {
                            childJumpDirection = "reverse";
                        }
                    }
                } else {
                    if (canv.keyboard.isUpArrow() || canv.keyboard.isDownArrow()) {
                        isChildJump = true;
                        if (canv.keyboard.isDownArrow()) {
                            childJumpDirection = "direct";
                        } else {
                            childJumpDirection = "reverse";
                        }
                    }
                }
            } else {
                if (this.directionMode === "horizontal") {
                    if (onlySelectedNode?.direction === "direct" && canv.keyboard.isRightArrow()) {
                        isChildJump = true;
                        childJumpDirection = "direct";
                    } else if (
                        onlySelectedNode?.direction === "reverse" &&
                        canv.keyboard.isLeftArrow()
                    ) {
                        isChildJump = true;
                        childJumpDirection = "reverse";
                    }
                } else {
                    if (onlySelectedNode?.direction === "direct" && canv.keyboard.isDownArrow()) {
                        isChildJump = true;
                        childJumpDirection = "direct";
                    } else if (
                        onlySelectedNode?.direction === "reverse" &&
                        canv.keyboard.isUpArrow()
                    ) {
                        isChildJump = true;
                        childJumpDirection = "reverse";
                    }
                }
            }

            if (isHorizontalModeNeighbourJump || isVerticalModeNeighbourJump) {
                const isPrevJump =
                    (this.directionMode === "horizontal" && canv.keyboard.isUpArrow()) ||
                    (this.directionMode === "vertical" && canv.keyboard.isLeftArrow());
                const isNextJump =
                    (this.directionMode === "horizontal" && canv.keyboard.isDownArrow()) ||
                    (this.directionMode === "vertical" && canv.keyboard.isRightArrow());

                if (onlySelectedNode.parentNodeUID === "") return; // для root не делаем

                const parentNode = this.findNode(onlySelectedNode.parentNodeUID);

                const children = this.getSortedChildren(
                    parentNode as MindMapWidgetNode,
                    onlySelectedNode.direction,
                );

                for (let i = 0; i < children.length; i++) {
                    if (children[i].UID === onlySelectedNode?.UID) {
                        if (isPrevJump && i !== 0) {
                            this.selected = [children[i - 1].UID];
                            canv.widgetMenu.reopen();
                        } else if (isNextJump && i !== children.length - 1) {
                            this.selected = [children[i + 1].UID];
                            canv.widgetMenu.reopen();
                        }
                    }
                }
            } else if (isParentJump) {
                this.selected = [onlySelectedNode?.parentNodeUID];
                canv.widgetMenu.reopen();
            } else if (isChildJump) {
                const children = this.getSortedChildren(onlySelectedNode, childJumpDirection);
                if (children.length !== 0) {
                    this.selected = [children[0].UID];
                    canv.widgetMenu.reopen();
                }
            }
        } else if (canv.keyboard.isEnter()) {
            if (this.selected.length !== 1) return;
            if (onlySelectedNode && this.interactionType === "select") {
                this.startNodeFocus(onlySelectedNode);
                onlySelectedNode.textPart.selection.selectAll();
            }
        }
    }

    draw() {
        for (let i = 0; i < this.nodes.length; i++) {
            const opacity = canv.ctx.globalAlpha;

            if (
                this.flyNode &&
                this.interactionType === "fly" &&
                this.isDeepChildRecursive(this.nodes[i], this.flyNode.UID)
            ) {
                canv.ctx.globalAlpha = 0.5;
            }

            this.nodes[i].draw();

            canv.ctx.globalAlpha = opacity;
        }

        this.drawSelectedOutline();
        this.drawLinks();
        this.interface.draw();
        this.drawFly();
        this.drawSelecting();

        if (this.interactionType === "fly") {
            canv.ctx.strokeStyle = "#0B99FF";
            canv.ctx.lineWidth = canv.actionManager.getZoomConstant(1);
            canv.ctx.strokeRect(
                canv.canvasController.startX,
                canv.canvasController.startY,
                canv.canvasController.width,
                canv.canvasController.height,
            );
        }
    }

    drawSelectedOutline() {
        if (this.interactionType === "fly") return;

        canv.ctx.strokeStyle = "#0B99FF";
        canv.ctx.lineWidth = canv.actionManager.getZoomConstant(2.5);

        const selectedProps = this.getSelectionProps();
        drawRoundedRect(
            selectedProps.startX,
            selectedProps.startY,
            selectedProps.width,
            selectedProps.height,
            canv.actionManager.getZoomConstant(4),
        );
        canv.ctx.stroke();

        for (let i = 0; i < this.selected.length; i++) {
            const node = this.findNode(this.selected[i]);
            if (node == null) continue;
            drawRoundedRect(
                node.startX,
                node.startY,
                node.width,
                node.height,
                canv.actionManager.getZoomConstant(4),
            );
            if (this.interactionType === "nodeFocus") {
                canv.ctx.strokeStyle = "#BCE3FF";
            } else {
                canv.ctx.strokeStyle = "#0B99FF";
            }
            canv.ctx.lineWidth = canv.actionManager.getZoomConstant(2.5);
            canv.ctx.stroke();
        }
    }

    drawLinks() {
        for (let i = 0; i < this.nodes.length; i++) {
            const node = this.nodes[i];
            if (node.parentNodeUID === "") continue;

            const parentNode = this.findNode(node.parentNodeUID);

            if (parentNode) {
                this.drawLink(node, parentNode, node.direction, this.directionMode);
            }
        }
    }

    drawLink(
        node: MindMapWidgetNode,
        parentNode: MindMapWidgetNode,
        direction: string,
        directionMode: string,
    ) {
        let drawData: MindMapDrawData = {
            fromX: 0,
            fromY: 0,
            toX: 0,
            toY: 0,
        };

        if (directionMode === "horizontal") {
            if (direction === "direct") {
                drawData = {
                    fromX: parentNode.startX + parentNode.width,
                    fromY: parentNode.startY + parentNode.height / 2,
                    toX: node.startX,
                    toY: node.startY + node.height / 2,
                };
            } else {
                drawData = {
                    fromX: node.startX + node.width,
                    fromY: node.startY + node.height / 2,
                    toX: parentNode.startX,
                    toY: parentNode.startY + parentNode.height / 2,
                };
            }
        } else {
            if (direction === "direct") {
                drawData = {
                    fromX: parentNode.startX + parentNode.width / 2,
                    fromY: parentNode.startY + parentNode.height,
                    toX: node.startX + node.width / 2,
                    toY: node.startY,
                };
            } else {
                drawData = {
                    fromX: node.startX + node.width / 2,
                    fromY: node.startY + node.height,
                    toX: parentNode.startX + parentNode.width / 2,
                    toY: parentNode.startY,
                };
            }
        }

        this.drawLinkLine(drawData, directionMode, node.color);
    }

    drawLinkLine(drawData: MindMapDrawData, directionMode: string, color: string) {
        canv.ctx.strokeStyle = color;
        canv.ctx.lineWidth = 3;

        if (directionMode === "horizontal") {
            // fromX меньше toX
            canv.ctx.beginPath();
            canv.ctx.moveTo(drawData.fromX, drawData.fromY);
            canv.ctx.bezierCurveTo(
                drawData.fromX + this.getNodeGap("co-direct") / 2,
                drawData.fromY,
                drawData.toX - this.getNodeGap("co-direct") / 2,
                drawData.toY,
                drawData.toX,
                drawData.toY,
            );
            canv.ctx.stroke();
            canv.ctx.closePath();
        } else {
            // fromX меньше toX
            canv.ctx.beginPath();
            canv.ctx.moveTo(drawData.fromX, drawData.fromY);
            canv.ctx.bezierCurveTo(
                drawData.fromX,
                drawData.fromY + this.getNodeGap("co-direct") / 2,
                drawData.toX,
                drawData.toY - this.getNodeGap("co-direct") / 2,
                drawData.toX,
                drawData.toY,
            );
            canv.ctx.stroke();
            canv.ctx.closePath();
        }
    }

    isOverAnyNode() {
        for (let i = 0; i < this.nodes.length; i++) {
            const node = this.nodes[i];
            if (isOverRect(node.startX, node.startY, node.width, node.height)) {
                return {
                    status: true,
                    nodeUID: node.UID,
                };
            }
        }

        return { status: false };
    }

    addNeighbour(node: MindMapWidgetNode, side: string) {
        const parentNode = this.findNode(node.parentNodeUID); // interface проверяет, что есть parentNodeUID
        const direction = node.direction;

        let nextNodeLevel: string | null = null;
        let prevNodeLevel: string | null = null;

        if (parentNode) {
            const parentNodeChildren = this.getSortedChildren(parentNode, direction);

            for (let i = 0; i < parentNodeChildren.length; i++) {
                if (parentNodeChildren[i].UID === node.UID) {
                    if (i !== parentNodeChildren.length - 1) {
                        nextNodeLevel = parentNodeChildren[i + 1].level;
                    }

                    if (i !== 0) {
                        prevNodeLevel = parentNodeChildren[i - 1].level;
                    }

                    break;
                }
            }
        }

        const newNode = new MindMapWidgetNode(0, 0, node.parentNodeUID, direction as Direction);

        if (side === "after") {
            newNode.level = generateKeyBetween(node.level, nextNodeLevel);
        } else {
            newNode.level = generateKeyBetween(prevNodeLevel, node.level);
        }

        newNode.color = node.color;
        this.nodes.push(newNode);

        canv.eventManager.openRecord();
        this.createBranchCreateEvent([newNode]);
        canv.eventManager.closeRecord();

        this.alignChildrenAll(newNode);
        this.updateDims();

        return newNode;
    }

    addChild(parentNode: MindMapWidgetNode, direction: Direction, shouldEvent = true) {
        const newNode = new MindMapWidgetNode(0, 0, parentNode.UID, direction);

        const children = this.getSortedChildren(parentNode, direction);
        if (children.length !== 0) {
            newNode.level = generateKeyBetween(children[children.length - 1].level, null);
        }

        newNode.color = parentNode.color;
        this.nodes.push(newNode);

        if (shouldEvent) {
            canv.eventManager.openRecord();
            this.createBranchCreateEvent([newNode]);
            canv.eventManager.closeRecord();
        }

        this.alignChildrenAll(parentNode);
        this.updateDims();
        return newNode;
    }

    alignChildrenAll(parentNode: MindMapWidgetNode) {
        this.alignChildren(parentNode, "direct");
        this.alignChildren(parentNode, "reverse");

        if (parentNode.parentNodeUID !== "") {
            const parentNodeParent = this.findNode(parentNode.parentNodeUID);
            if (parentNodeParent != null) {
                this.alignChildrenAll(parentNodeParent);
            }
        }
    }

    alignChildren(parentNode: MindMapWidgetNode, direction: Direction) {
        const children = this.getSortedChildren(parentNode, direction);

        let rDirectionSize = 0;
        for (let i = 0; i < children.length; i++) {
            const data = this.calculateBranchBox(children[i], direction);
            if (this.directionMode === "horizontal") {
                rDirectionSize += data.maxY - data.minY;
            } else {
                rDirectionSize += data.maxX - data.minX;
            }

            if (i !== children.length - 1) {
                rDirectionSize += this.getNodeGap("not-co-direct");
            }
        }

        let rDirectionPosition = 0;
        if (this.directionMode === "horizontal") {
            rDirectionPosition = parentNode.startY + parentNode.height / 2 - rDirectionSize / 2;
        } else {
            rDirectionPosition = parentNode.startX + parentNode.width / 2 - rDirectionSize / 2;
        }

        for (let i = 0; i < children.length; i++) {
            const branchBox = this.calculateBranchBox(children[i], direction);

            // считаем постоянную позицию
            let dDirectionPosition = 0;
            if (this.directionMode === "horizontal") {
                if (direction === "direct") {
                    dDirectionPosition =
                        parentNode.startX + parentNode.width + this.getNodeGap("co-direct");
                } else {
                    dDirectionPosition =
                        parentNode.startX - this.getNodeGap("co-direct") - children[i].width;
                }
            } else {
                if (direction === "direct") {
                    dDirectionPosition =
                        parentNode.startY + parentNode.height + this.getNodeGap("co-direct");
                } else {
                    dDirectionPosition =
                        parentNode.startY - this.getNodeGap("co-direct") - children[i].height;
                }
            }

            if (this.directionMode === "horizontal") {
                this.moveBranchRecursive(
                    children[i],
                    dDirectionPosition - children[i].startX,
                    rDirectionPosition - branchBox.minY,
                );
                rDirectionPosition += branchBox.maxY - branchBox.minY;
            } else {
                this.moveBranchRecursive(
                    children[i],
                    rDirectionPosition - branchBox.minX,
                    dDirectionPosition - children[i].startY,
                );
                rDirectionPosition += branchBox.maxX - branchBox.minX;
            }

            rDirectionPosition += this.getNodeGap("not-co-direct");
        }
    }

    isDeepChildRecursive(node: MindMapWidgetNode, checkingUID: string) {
        // использовать для opacity flyNode branch и при выборе flyParentCandidate

        if (node.UID === checkingUID) {
            return true;
        }

        if (node.parentNodeUID !== "") {
            const parent = this.findNode(node.parentNodeUID);
            if (parent == null) return true; // такого быть не должно

            return this.isDeepChildRecursive(parent, checkingUID);
        }

        return false;
    }

    setFlyData(parentNodeUID: string, direction: string, foreignMindMap?: string) {
        this.flyData = {
            parentNodeUID: parentNodeUID,
            direction: direction,
            foreignMindMap: foreignMindMap,
        };
    }

    startFly() {
        const onlySelected = this.getOnlySelectedNode();

        this.flyNode = new MindMapWidgetNode(0, 0);

        if (onlySelected) {
            this.flyNode.loadState(onlySelected.getState("full"), "full");
        }

        this.setFlyData(this.flyNode.parentNodeUID, this.flyNode.direction, undefined);
        canv.widgetMenu.hide();
    }

    applyFly() {
        if (!this.flyNode) return;

        const pt = canv.actionManager.getActionPoint();
        const prePt = canv.actionManager.getPreActionPoint();

        this.flyNode.moveNode(pt.x - prePt.x, pt.y - prePt.y);

        const result = this.calculateFlyParent(this.flyNode);

        if (result.parentNodeUID !== "") {
            this.setFlyData(result.parentNodeUID, result.direction, undefined);
            return;
        }

        // далее ищем в других mindMaps

        for (let i = 0; i < canv.canvasController.items.length; i++) {
            const group = canv.canvasController.items[i];

            if (!group.isGroupOnScreen()) continue;

            const mindMap = group.onlyChild() as MindMapWidget;

            if (!group.isItemWrapper() || mindMap.constructorName !== "mindMap") continue;

            const candidateResult = mindMap.calculateFlyParent(this.flyNode);

            if (candidateResult.parentNodeUID !== "") {
                this.setFlyData(
                    candidateResult.parentNodeUID,
                    candidateResult.direction,
                    group.onlyChild()?.UID,
                );
                return;
            }
        }

        // сюда доходим только если нигде не нашли parent candidate

        this.setFlyData("", "", undefined);
    }

    stopFly() {
        if (!this.flyNode || !this.flyData) return;

        canv.widgetMenu.reopen();

        const flyNodeOriginal = this.findNode(this.flyNode.UID);

        if (flyNodeOriginal == null) return; // Если кто-то удалил ноду, которую мы двигаем.

        if (this.flyData?.parentNodeUID !== "") {
            canv.eventManager.openRecord();

            // удаляем ноду из this
            let branchNodes: MindMapWidgetNode[] = [];
            // смотрим именно на this.flyNode.parentNodeUID, он не меняется
            if (flyNodeOriginal.parentNodeUID !== "") {
                // ветка
                branchNodes = this.deleteBranch(flyNodeOriginal);
                this.alignMindMap();
            } else {
                // root
                canv.canvasController.deleteProcessor(this.parent);
                branchNodes = this.nodes;
            }

            // добавляем ноду в this или foreign -
            let mindMapToAdd: MindMapWidget = this;

            if (this.flyData?.foreignMindMap !== undefined) {
                mindMapToAdd = canv.canvasController.FindByUID(
                    this.flyData.foreignMindMap,
                ) as MindMapWidget;

                if (mindMapToAdd == null) {
                    // если кто-то удалил mindMap
                    canv.eventManager.closeRecord(); // нужно сохранить удаление ветки
                    return;
                }
            }

            const parentNode = mindMapToAdd.findNode(this.flyData.parentNodeUID);
            if (parentNode == null) {
                // если кто-то удалил node
                canv.eventManager.closeRecord(); // нужно сохранить удаление ветки
                return;
            }

            // определяем level
            const parentNodeChildren = mindMapToAdd.getSortedChildren(
                parentNode,
                this.flyData.direction,
            );
            let rMin: string | null = null;
            let rMax: string | null = null;

            for (let i = 0; i < parentNodeChildren.length; i++) {
                if (mindMapToAdd.directionMode === "horizontal") {
                    if (this.flyNode.startY > parentNodeChildren[i].startY) {
                        rMin = parentNodeChildren[i].level;
                    } else {
                        rMax = parentNodeChildren[i].level;
                        break;
                    }
                } else {
                    if (this.flyNode.startX > parentNodeChildren[i].startX) {
                        rMin = parentNodeChildren[i].level;
                    } else {
                        rMax = parentNodeChildren[i].level;
                        break;
                    }
                }
            }

            branchNodes[0].parentNodeUID = this.flyData.parentNodeUID;
            branchNodes[0].level = generateKeyBetween(rMin, rMax);
            for (let i = 0; i < branchNodes.length; i++) {
                branchNodes[i].direction = this.flyData.direction as Direction;
                mindMapToAdd.nodes.push(branchNodes[i]);
            }

            mindMapToAdd.alignMindMap();
            mindMapToAdd.createBranchCreateEvent(branchNodes);
            canv.eventManager.closeRecord();

            if (this.flyData.foreignMindMap !== undefined) {
                canv.toolBox
                    .TryGetToolByName("cursorTool")
                    .setSelectedAndInteraction(mindMapToAdd.parent);
                mindMapToAdd.getOnlySelectedNode()?.textPart.unfocusText();
                mindMapToAdd.interactionType = "select";
                mindMapToAdd.selected = [branchNodes[0].UID];
                canv.widgetMenu.reopen();
            } else {
                canv.widgetMenu.TryLocateMenu();
            }
        } else {
            if (flyNodeOriginal.parentNodeUID === "") {
                canv.eventManager.openRecord();
                const anchorGroups = queryAreaByGroup(this.parent, false, true);
                const dx = this.flyNode.startX - flyNodeOriginal.startX;
                const dy = this.flyNode.startY - flyNodeOriginal.startY;

                this.fixState("rootMove");
                this.translate(dx, dy);
                this.saveState("rootMove");
                this.alignMindMap();

                if (anchorGroups.length) {
                    for (let i = 0; i < anchorGroups.length; i++) {
                        anchorGroups[i].fixWidgets("move", false);
                    }

                    for (let i = 0; i < anchorGroups.length; i++) {
                        anchorGroups[i].translateGroup(dx, dy);
                    }

                    for (let i = 0; i < anchorGroups.length; i++) {
                        anchorGroups[i].saveWidgets("move", false);
                    }
                }

                canv.eventManager.closeRecord();
            } else {
                this.selected = []; // это сделается в stopInteraction, но прописываем явно
                canv.eventManager.openRecord();
                const branchNodes = this.deleteBranch(flyNodeOriginal);
                this.alignMindMap();

                // Делаем старшую ноду ноду root (parentNodeUID и direction используются как признаки root)
                // это надо сделать до newMindMap.getRootNode()
                branchNodes[0].parentNodeUID = "";
                branchNodes[0].direction = "";

                const newMindMap = new MindMapWidget(
                    flyNodeOriginal.startX,
                    flyNodeOriginal.startY,
                );
                newMindMap.nodes = branchNodes;
                newMindMap
                    ?.getRootNode()
                    ?.moveNode(
                        this.flyNode.startX - flyNodeOriginal.startX,
                        this.flyNode.startY - flyNodeOriginal.startY,
                    );
                newMindMap.alignMindMap();

                const newGroup = new Group(true);
                newMindMap.setParent(newGroup);
                canv.canvasController.addGroup(newGroup);

                canv.eventManager.createBuildEvent("create", newGroup);
                canv.eventManager.closeRecord();

                canv.toolBox.TryGetToolByName("cursorTool").setSelectedAndInteraction(newGroup);

                canv.eventManager.closeRecord();
            }
        }
    }

    drawFly() {
        if (!this.flyNode || this.interactionType !== "fly") return;

        this.flyNode.draw();

        if (!this.flyData || this.flyData.parentNodeUID === "") return;

        let parentNode: MindMapWidgetNode | null = null;
        let directionMode = "horizontal";

        if (this.flyData.foreignMindMap !== undefined) {
            const foreignMindMap = canv.canvasController.FindByUID(
                this.flyData.foreignMindMap,
            ) as MindMapWidget;

            if (foreignMindMap == null) return;

            parentNode = foreignMindMap.findNode(this.flyData.parentNodeUID);
            if (parentNode == null) return;

            directionMode = foreignMindMap.directionMode;
        } else {
            parentNode = this.findNode(this.flyData.parentNodeUID);
            if (parentNode == null) return;

            directionMode = this.directionMode;
        }

        this.drawLink(this.flyNode, parentNode, this.flyData.direction, directionMode);
    }

    calculateFlyParent(flyNode: MindMapWidgetNode) {
        const directParent = this.calculateFlyParentDirection(flyNode, "direct");
        const reverseParent = this.calculateFlyParentDirection(flyNode, "reverse");

        const result = {
            parentNodeUID: "",
            direction: "",
            foreign: undefined,
        };

        if (directParent.status) {
            result.parentNodeUID = directParent.nodeUID;
            result.direction = "direct";

            // this.flyNode.parentNodeUID = directParent["nodeUID"];
            // this.flyNode.direction = "direct";
        } else if (reverseParent.status) {
            result.parentNodeUID = reverseParent.nodeUID;
            result.direction = "reverse";

            // this.flyNode.parentNodeUID = reverseParent["nodeUID"];
            // this.flyNode.direction = "reverse";
        } else {
            // this.flyNode.parentNodeUID = "";
            // this.flyNode.direction = "";
        }

        return result;
    }

    calculateFlyParentDirection(flyNode: MindMapWidgetNode, direction: Direction) {
        let result: FlyParentDirection = {
            status: false,
        };

        let dBorder = 0;

        for (let i = 0; i < this.nodes.length; i++) {
            if (this.isDeepChildRecursive(this.nodes[i], flyNode.UID)) continue;

            if (this.nodes[i].direction !== "" && this.nodes[i].direction !== direction) continue;

            const status = this.calculateFlyParentDirectionCheck(flyNode, this.nodes[i], direction);

            let priorityState = false;
            let priorityValue = 0;
            if (this.directionMode === "horizontal") {
                if (direction === "direct") {
                    priorityValue = this.nodes[i].startX;
                    priorityState = priorityValue > dBorder;
                } else {
                    priorityValue = this.nodes[i].startX + this.nodes[i].width;
                    priorityState = priorityValue < dBorder;
                }
            } else {
                if (direction === "direct") {
                    priorityValue = this.nodes[i].startY;
                    priorityState = priorityValue > dBorder;
                } else {
                    priorityValue = this.nodes[i].startY + this.nodes[i].height;
                    priorityState = priorityValue < dBorder;
                }
            }

            if (status && (!result.status || priorityState)) {
                dBorder = priorityValue;
                result = {
                    status: true,
                    nodeUID: this.nodes[i].UID,
                };
            }
        }

        return result;
    }

    calculateFlyParentDirectionCheck(
        flyNode: MindMapWidgetNode,
        node: MindMapWidgetNode,
        direction: Direction,
    ) {
        const pt = canv.actionManager.getActionPoint();

        const box = this.calculateBranchBox(node, direction);

        if (this.directionMode === "horizontal") {
            const yMinBorder = box.minY - this.getNodeGap("not-co-direct") / 2;
            const yMaxBorder = box.maxY + this.getNodeGap("not-co-direct") / 2;

            if (direction === "direct") {
                if (
                    pt.y >= yMinBorder &&
                    pt.y <= yMaxBorder &&
                    flyNode.startX >= box.minX &&
                    flyNode.startX <= box.maxX + this.getNodeGap("co-direct")
                ) {
                    return true;
                }
            } else {
                if (
                    pt.y >= yMinBorder &&
                    pt.y <= yMaxBorder &&
                    flyNode.startX + flyNode.width >= box.minX - this.getNodeGap("co-direct") &&
                    flyNode.startX + flyNode.width <= box.maxX
                ) {
                    return true;
                }
            }
        } else {
            const xMinBorder = box.minX - this.getNodeGap("not-co-direct") / 2;
            const xMaxBorder = box.maxX + this.getNodeGap("not-co-direct") / 2;

            if (direction === "direct") {
                if (
                    pt.x >= xMinBorder &&
                    pt.x <= xMaxBorder &&
                    flyNode.startY >= box.minY &&
                    flyNode.startY <= box.maxY + this.getNodeGap("co-direct")
                ) {
                    return true;
                }
            } else {
                if (
                    pt.x >= xMinBorder &&
                    pt.x <= xMaxBorder &&
                    flyNode.startY + flyNode.height >= box.minY - this.getNodeGap("co-direct") &&
                    flyNode.startY + flyNode.height <= box.maxY
                ) {
                    return true;
                }
            }
        }

        return false;
    }

    collectAndRemoveBranch(
        node: MindMapWidgetNode,
        nodes: MindMapWidgetNode[] | undefined = undefined,
    ) {
        if (nodes !== undefined) {
            nodes.push(node);
        }

        const children = this.getChildren(node, node.direction);
        for (let i = 0; i < children.length; i++) {
            this.collectAndRemoveBranch(children[i], nodes);
        }

        this.removeNode(node.UID);
    }

    removeNode(nodeUID: string) {
        for (let i = 0; i < this.nodes.length; i++) {
            if (this.nodes[i].UID === nodeUID) {
                this.nodes.splice(i, 1);
                break;
            }
        }
    }

    isSelectingAction() {
        this.nodesSelectingData = {
            startX: selectionManager.selectingStartX,
            startY: selectionManager.selectingStartY,
            width: selectionManager.selectingWidth,
            height: selectionManager.selectingHeight,
            simulatedStartX: selectionManager.selectingStartX,
            simulatedStartY: selectionManager.selectingStartY,
            simulatedWidth: 0,
            simulatedHeight: 0,
        };

        return this.getNodesUnderSelecting();
    }

    updateSelecting() {
        const pt = canv.actionManager.getActionPoint();
        const prePt = canv.actionManager.getPreActionPoint();

        this.nodesSelectingData.width += pt.x - prePt.x;
        this.nodesSelectingData.height += pt.y - prePt.y;

        this.selected = this.getNodesUnderSelecting();
    }

    getNodesUnderSelecting() {
        this.nodesSelectingData.simulatedStartX = this.nodesSelectingData.startX;
        this.nodesSelectingData.simulatedStartY = this.nodesSelectingData.startY;

        if (this.nodesSelectingData.width < 0) {
            this.nodesSelectingData.simulatedStartX =
                this.nodesSelectingData.startX + this.nodesSelectingData.width; // именно +, так как сам width отрицательный
        }

        if (this.nodesSelectingData.height < 0) {
            this.nodesSelectingData.simulatedStartY =
                this.nodesSelectingData.startY + this.nodesSelectingData.height;
        }

        this.nodesSelectingData.simulatedWidth = Math.abs(this.nodesSelectingData.width);
        this.nodesSelectingData.simulatedHeight = Math.abs(this.nodesSelectingData.height);

        const underSelectingNodes: string[] = [];

        for (let i = 0; i < this.nodes.length; i++) {
            const node = this.nodes[i];

            if (
                this.nodesSelectingData.simulatedStartX <= node.startX + node.width &&
                this.nodesSelectingData.simulatedStartY <= node.startY + node.height &&
                this.nodesSelectingData.simulatedStartX + this.nodesSelectingData.simulatedWidth >=
                    node.startX &&
                this.nodesSelectingData.simulatedStartY + this.nodesSelectingData.simulatedHeight >=
                    node.startY
            ) {
                underSelectingNodes.push(node.UID);
            }
        }

        return underSelectingNodes;
    }

    drawSelecting() {
        if (this.interactionType !== "selecting") return;

        canv.ctx.strokeStyle = "#0B99FF";
        canv.ctx.lineWidth = 2 / canv.m[0];
        canv.ctx.strokeRect(
            this.nodesSelectingData.startX,
            this.nodesSelectingData.startY,
            this.nodesSelectingData.width,
            this.nodesSelectingData.height,
        );

        canv.ctx.fillStyle = "#0B99FF";
        canv.ctx.globalAlpha = 0.2;
        canv.ctx.lineWidth = 2 / canv.m[0];
        canv.ctx.fillRect(
            this.nodesSelectingData.startX,
            this.nodesSelectingData.startY,
            this.nodesSelectingData.width,
            this.nodesSelectingData.height,
        );
        canv.ctx.globalAlpha = 1;
    }

    translate(x: number, y: number) {
        if (isOnScreenAfterTranslate(x, y, this)) {
            for (let i = 0; i < this.nodes.length; i++) {
                this.nodes[i].moveNode(x, y);
            }
        }

        this.updateDims();
    }

    isOver() {
        return this.isOverAnyNode().status;
    }

    isInArea(area: ElementBaseArea) {
        for (let i = 0; i < this.nodes.length; i++) {
            const node = this.nodes[i];

            if (
                area.x <= node.startX + node.width &&
                area.y <= node.startY + node.height &&
                area.x + area.width >= node.startX &&
                area.y + area.height >= node.startY
            ) {
                return true;
            }
        }

        return false;
    }

    shouldDraw() {
        // переделать на isAnyNodeOnScreen();
        return true;
    }

    locateWidgetMenu() {
        if (this.selected.length !== 0) {
            const props = this.getSelectionProps();
            return {
                status: true,
                x: props.startX + props.width / 2,
                y: props.startY,
            };
        } else {
            // сюда попадем при selecting ничего не выбрав
            return {
                status: true,
                x: this.startX + this.width / 2,
                y: this.startY,
            };
        }
    }

    regenerateUIDS() {
        const rootNode = this.getRootNode();

        if (!rootNode) return;

        const directChildren = this.getChildren(rootNode, "direct");
        const reverseChildren = this.getChildren(rootNode, "reverse");

        rootNode.UID = generateUID();
        regenerateTextPartUID(rootNode.textPart, rootNode.UID);

        for (let i = 0; i < directChildren.length; i++) {
            this.regenerateBranchUIDs(directChildren[i], rootNode.UID);
        }

        for (let i = 0; i < reverseChildren.length; i++) {
            this.regenerateBranchUIDs(reverseChildren[i], rootNode.UID);
        }
    }

    searchText(text: string) {
        const result: SearchTextObject[] = [];

        for (let i = 0; i < this.nodes.length; i++) {
            const nodeText = this.nodes[i].textPart.getText().toLowerCase();

            if (nodeText.includes(text)) {
                result.push({
                    status: true,
                    textWidget: this.nodes[i].textPart,
                    mode: "text",
                });
            }
        }

        return result;
    }

    getRootNode() {
        for (let i = 0; i < this.nodes.length; i++) {
            if (this.nodes[i].parentNodeUID === "") {
                return this.nodes[i];
            }
        }
    }

    getOnlySelectedNode() {
        return this.findNode(this.selected[0]);
    }

    findNode(nodeUID: string) {
        // Тут всегда должна находиться нода
        for (let i = 0; i < this.nodes.length; i++) {
            if (this.nodes[i].UID === nodeUID) {
                return this.nodes[i];
            }
        }

        return null;
    }

    getSortedChildren(node: MindMapWidgetNode, direction: string) {
        const children = this.getChildren(node, direction);

        this.sortByLevel(children);

        return children;
    }

    isOverAnySelected() {
        for (let i = 0; i < this.selected.length; i++) {
            const node = this.findNode(this.selected[i]);

            if (node && isOverRect(node.startX, node.startY, node.width, node.height)) {
                return true;
            }
        }

        return false;
    }

    getSelectedNodes() {
        const nodes: MindMapWidgetNode[] = [];

        for (let i = 0; i < this.selected.length; i++) {
            const node = this.findNode(this.selected[i]);

            if (node == null) continue; // такого быть не должно

            nodes.push(node);
        }

        return nodes;
    }

    getSelectionProps(specifiedNodes: MindMapWidgetNode[] | undefined = undefined) {
        let selectedNodes = this.getSelectedNodes();

        let minX: number | undefined;
        let maxX: number | undefined;
        let minY: number | undefined;
        let maxY: number | undefined;

        if (specifiedNodes !== undefined) {
            selectedNodes = specifiedNodes;
        }

        for (let i = 0; i < selectedNodes.length; i++) {
            const node = selectedNodes[i];

            if (minX === undefined || node.startX < minX) minX = node.startX;
            if (maxX === undefined || node.startX + node.width > maxX)
                maxX = node.startX + node.width;

            if (minY === undefined || node.startY < minY) minY = node.startY;
            if (maxY === undefined || node.startY + node.height > maxY)
                maxY = node.startY + node.height;
        }

        return {
            startX: minX,
            startY: minY,
            width: (maxX as number) - (minX as number),
            height: (maxY as number) - (minY as number),
        } as MindMapSelectionProps;
    }

    alignMindMap() {
        const rootNode = this.getRootNode();

        if (!rootNode) return;

        const directChildren = this.getChildren(rootNode, "direct");
        for (let i = 0; i < directChildren.length; i++) {
            this.alignBranchRecursive(directChildren[i]);
        }
        this.alignChildren(rootNode, "direct");

        const reverseChildren = this.getChildren(rootNode, "reverse");
        for (let i = 0; i < reverseChildren.length; i++) {
            this.alignBranchRecursive(reverseChildren[i]);
        }
        this.alignChildren(rootNode, "reverse");

        this.updateDims();
    }

    alignBranchRecursive(node: MindMapWidgetNode) {
        const children = this.getChildren(node, node.direction);
        for (let i = 0; i < children.length; i++) {
            this.alignBranchRecursive(children[i]);
        }

        this.alignChildren(node, node.direction as Direction);
    }

    isInSelected(nodeUID: string) {
        for (let i = 0; i < this.selected.length; i++) {
            if (this.selected[i] === nodeUID) {
                return true;
            }
        }

        return false;
    }

    removeFromSelected(nodeUID: string) {
        for (let i = 0; i < this.selected.length; i++) {
            if (this.selected[i] === nodeUID) {
                this.selected.splice(i, 1);
                break;
            }
        }
    }

    toggleSelected() {
        for (let i = 0; i < this.nodes.length; i++) {
            const node = this.nodes[i];
            if (isOverRect(node.startX, node.startY, node.width, node.height)) {
                if (this.isInSelected(node.UID)) {
                    this.removeFromSelected(node.UID);
                } else {
                    this.selected.push(node.UID);
                }
            }
        }
    }

    sortByLevel(container: MindMapWidgetNode[]) {
        const compare = (a: MindMapWidgetNode, b: MindMapWidgetNode) => {
            if (a.level < b.level) {
                return -1;
            }
            if (a.level > b.level) {
                return 1;
            }
            return 0;
        };

        container.sort(compare);
    }

    getNodeGap(directType: string) {
        if (directType === "co-direct") {
            return 70;
        } else {
            return 35;
        }
    }

    getChildren(node: MindMapWidgetNode, direction: string) {
        const children: MindMapWidgetNode[] = [];

        for (let i = 0; i < this.nodes.length; i++) {
            if (this.nodes[i].parentNodeUID === node.UID && this.nodes[i].direction === direction) {
                children.push(this.nodes[i]);
            }
        }

        return children;
    }

    calculateBranchBox(node: MindMapWidgetNode, direction: Direction) {
        const data: MindMapCalculateBranchBoxData = {
            minX: node.startX,
            maxX: node.startX + node.width,
            minY: node.startY,
            maxY: node.startY + node.height,
        };

        this.calculateBranchBoxRecursive(node, direction, data);

        return data;
    }

    calculateBranchBoxRecursive(
        node: MindMapWidgetNode,
        direction: Direction,
        data: MindMapCalculateBranchBoxData,
    ) {
        if (node.startX < data.minX) data.minX = node.startX;
        if (node.startX + node.width > data.maxX) data.maxX = node.startX + node.width;
        if (node.startY < data.minY) data.minY = node.startY;
        if (node.startY + node.height > data.maxY) data.maxY = node.startY + node.height;

        const children = this.getChildren(node, direction);
        for (let i = 0; i < children.length; i++) {
            this.calculateBranchBoxRecursive(children[i], direction, data);
        }
    }

    moveBranchRecursive(node: MindMapWidgetNode, moveX: number, moveY: number) {
        node.moveNode(moveX, moveY);
        const children = this.getChildren(node, node.direction);

        for (let i = 0; i < children.length; i++) {
            this.moveBranchRecursive(children[i], moveX, moveY);
        }
    }

    setBranchBuildFixData(nodes: MindMapWidgetNode[], actionType: string) {
        this.branchBuildFixData.action = actionType;

        this.branchBuildFixData.nodes = [];

        for (let i = 0; i < nodes.length; i++) {
            this.branchBuildFixData.nodes.push(nodes[i].getState("full"));
        }
    }

    createBranchCreateEvent(nodes: MindMapWidgetNode[]) {
        this.setBranchBuildFixData(nodes, "delete"); // сначала delete для обратного действия
        this.fixState("nodeBuild");
        this.setBranchBuildFixData(nodes, "create"); // create для отправки
        this.saveState("nodeBuild");
    }

    createBranchDeleteEvent(nodes: MindMapWidgetNode[]) {
        this.setBranchBuildFixData(nodes, "create"); // сначала create для обратного действия
        this.fixState("nodeBuild");
        this.setBranchBuildFixData(nodes, "delete"); // delete для отправки
        this.saveState("nodeBuild");
    }

    deleteSelectedNodes() {
        const rootNode = this.getRootNode();
        let rootIsSelected = false;
        const topNodes: MindMapWidgetNode[] = [];

        for (let i = 0; i < this.selected.length; i++) {
            if (this.selected[i] === rootNode?.UID) rootIsSelected = true;
            const node = this.findNode(this.selected[i]);

            if (node && !this.isAnyAncestorInSelected(node)) {
                topNodes.push(node);
            }
        }

        if (rootIsSelected) {
            canv.eventManager.openRecord();
            canv.canvasController.deleteProcessor(this.parent);
            canv.eventManager.closeRecord();
            selectionManager.clearSelected(); // здесь сбросится interaction и вызывается stopInteraction. Для interactionType==select ничего не произойдет (event не бросится). Это важно даже не смотря на то, что виджет уже удален. Чтобы не выбросить event на удаленный виджет

            return;
        }

        canv.eventManager.openRecord();
        for (let i = 0; i < topNodes.length; i++) {
            this.deleteBranch(topNodes[i]);
        }
        canv.eventManager.closeRecord();

        // тут всегда interactionType == select и !rootIsSelected
        this.selected = [topNodes[0].parentNodeUID];
        canv.widgetMenu.reopen(); // после любого изменения this.selected нужно делать widgetMenu.reopen. Кроме updateSelecting

        this.alignMindMap();
    }

    isAnyAncestorInSelected(node: MindMapWidgetNode) {
        for (let i = 0; i < this.selected.length; i++) {
            if (this.selected[i] === node.UID) continue;

            if (this.isDeepChildRecursive(node, this.selected[i])) {
                return true;
            }
        }

        return false;
    }

    // не работает для root! Для root не работает все, где вызывается getChildren. Можно поискать getChildren и подумать не может ли вызваться для root.
    deleteBranch(topNode: MindMapWidgetNode) {
        const nodes: MindMapWidgetNode[] = [];

        this.collectAndRemoveBranch(topNode, nodes);
        this.createBranchDeleteEvent(nodes);

        return nodes;
    }

    copySelectedNodes() {
        const rootNode = this.getRootNode();
        let rootIsSelected = false;
        const topNodes: MindMapWidgetNode[] = [];

        for (let i = 0; i < this.selected.length; i++) {
            if (this.selected[i] === rootNode?.UID) rootIsSelected = true;
            const node = this.findNode(this.selected[i]);

            if (node && !this.isAnyAncestorInSelected(node)) {
                topNodes.push(node);
            }
        }

        const allStates: any[][] = [];

        if (!rootIsSelected) {
            for (let i = 0; i < topNodes.length; i++) {
                const cloneNodes: MindMapWidgetNode[] = [];

                this.cloneBranch(topNodes[i], "", cloneNodes);
                cloneNodes[0].direction = "";

                const newMindMap = new MindMapWidget(0, 0);
                newMindMap.nodes = cloneNodes;
                newMindMap.directionMode = this.directionMode;

                const newGroup = new Group(true);
                newMindMap.setParent(newGroup);

                newGroup.indexByParent = this.parent.indexByParent;

                const states: any[] = [];

                states.push(newGroup.getState("full"));
                states.push(newMindMap.getState("full"));

                allStates.push(states);
            }
        } else {
            const states: any[] = [];

            states.push(this.parent.getState("full"));
            states.push(this.getState("full"));

            allStates.push(states);
        }

        copyProccessorExec(allStates);
    }

    cloneBranch(node: MindMapWidgetNode, parentUID: string, cloneNodes: MindMapWidgetNode[]) {
        const clone = new MindMapWidgetNode(0, 0);
        cloneNodes.push(clone);
        clone.loadState(node.getState("full"), "full");
        clone.parentNodeUID = parentUID;
        clone.UID = generateUID(); // нужно, так как loadState загружает UID

        const children = this.getChildren(node, node.direction);
        for (let i = 0; i < children.length; i++) {
            this.cloneBranch(children[i], clone.UID, cloneNodes);
        }
    }

    regenerateBranchUIDs(node: MindMapWidgetNode, parentNodeUID: string) {
        const children = this.getChildren(node, node.direction);

        node.UID = generateUID();
        node.parentNodeUID = parentNodeUID;
        regenerateTextPartUID(node.textPart, node.UID);

        for (let i = 0; i < children.length; i++) {
            this.regenerateBranchUIDs(children[i], node.UID);
        }
    }

    getSelectedOrAllNodes() {
        if (this.interactionType !== "") {
            const selectedNodes: MindMapWidgetNode[] = [];

            for (let i = 0; i < this.selected.length; i++) {
                const node = this.findNode(this.selected[i]);

                if (node === null) continue; // такого быть не должно

                selectedNodes.push(node);
            }

            return selectedNodes;
        } else {
            return this.nodes;
        }
    }

    getWidgetOptions() {
        const widgetOptions: widgetOption[] = [];
        const rootNode = this.getRootNode();

        const nodes = this.getSelectedOrAllNodes();

        for (let i = 0; i < nodes.length; i++) {
            if (rootNode && nodes[i].UID !== rootNode.UID) {
                // при смене цвета линии у родительской ноды - ничего не происходит
                widgetOptions.push(new widgetOption("color", nodes[i].color, this.UID));
            }

            const { firstColor, isBold, isStriked, isUnderline, isItalic, hasBulletList } =
                nodes[i].textPart.styleSummary;

            widgetOptions.push(new widgetOption("textColor", firstColor, this.UID));
            widgetOptions.push(new widgetOption("textItalic", isItalic, this.UID));
            widgetOptions.push(new widgetOption("textUnderline", isUnderline, this.UID));
            widgetOptions.push(new widgetOption("textBold", isBold, this.UID));
            widgetOptions.push(new widgetOption("textStrike", isStriked, this.UID));
            widgetOptions.push(new widgetOption("textBulleted", hasBulletList, this.UID));

            widgetOptions.push(new widgetOption("textSize", nodes[i].textPart.fontSize, this.UID));
        }

        if (
            this.selected.length === 1 &&
            this.getOnlySelectedNode()?.UID === this.getRootNode()?.UID
        ) {
            widgetOptions.push(new widgetOption("mindMapDirection", this.directionMode, this.UID));
        }

        if (this.interactionType === "nodeFocus") {
            const node = this.getOnlySelectedNode();

            if (node) {
                widgetOptions.push(
                    new widgetOption("link", node.textPart.styleSummary.link, this.UID),
                );
            }
        } else if (
            (this.interactionType === "fastSelect" || this.interactionType === "select") &&
            nodes.length === 1
        ) {
            const node = this.getOnlySelectedNode();

            if (node) {
                widgetOptions.push(
                    new widgetOption("link", node.textPart.styleSummary.link, this.UID),
                );
            }
        }

        if (this.selected.length !== 0) {
            widgetOptions.push(new widgetOption("delete", this, this.UID));
        }

        return widgetOptions;
    }

    getHandlerForOption(name: string) {
        if (name === "color") {
            return this.menuColor;
        } else if (name === "textColor") {
            return this.menuTextColor;
        } else if (name === "textSize") {
            return this.menuTextSize;
        } else if (name === "textBold") {
            return this.menuTextBold;
        } else if (name === "textStrike") {
            return this.menuTextStrike;
        } else if (name === "textItalic") {
            return this.menuTextItalic;
        } else if (name === "textUnderline") {
            return this.menuTextUnderline;
        } else if (name === "textBulleted") {
            return this.menuTextBulleted;
        } else if (name === "mindMapDirection") {
            return this.menuDirectionMode;
        } else if (name === "link") {
            return this.menuLink;
        }
    }

    menuLink(currentLink: string) {
        const node = this.getOnlySelectedNode();

        if (node) {
            if (this.interactionType === "nodeFocus") {
                node.textPart.menuTextLink(currentLink);
            } else if (
                (this.interactionType === "fastSelect" || this.interactionType === "select") &&
                this.selected.length === 1
            ) {
                this.startNodeFocus(node);
                canv.textEditingController.selectAll();
                node.textPart.menuTextLink(currentLink);
            }
        }
    }

    menuColor(color: string) {
        const nodes = this.getSelectedOrAllNodes();

        for (let i = 0; i < nodes.length; i++) {
            nodes[i].color = color;
        }
    }

    menuTextColor(color: string) {
        const nodes = this.getSelectedOrAllNodes();

        for (let i = 0; i < nodes.length; i++) {
            nodes[i].textPart.menuTextColor(color, true);
        }
    }

    menuTextSize(value: number) {
        const nodes = this.getSelectedOrAllNodes();

        for (let i = 0; i < nodes.length; i++) {
            nodes[i].textPart.menuTextSize(value);
            nodes[i].updateDims();
        }

        this.alignMindMap();
    }

    menuTextBulleted(value: boolean) {
        const nodes = this.getSelectedOrAllNodes();

        for (let i = 0; i < nodes.length; i++) {
            nodes[i].textPart.menuTextBulleted(value);
            nodes[i].updateDims();
        }

        this.updateDims();
    }

    menuTextBold(value: boolean) {
        const nodes = this.getSelectedOrAllNodes();

        for (let i = 0; i < nodes.length; i++) {
            nodes[i].textPart.menuTextBold(value);
            nodes[i].updateDims();
        }

        this.updateDims();
    }

    menuTextStrike(value: boolean) {
        const nodes = this.getSelectedOrAllNodes();

        for (let i = 0; i < nodes.length; i++) {
            nodes[i].textPart.menuTextStrike(value);
            nodes[i].updateDims();
        }

        this.updateDims();
    }

    menuTextItalic(value: boolean) {
        const nodes = this.getSelectedOrAllNodes();

        for (const node of nodes) {
            node.textPart.menuTextItalic(value);
            node.textPart.updateDims();
        }

        this.updateDims();
    }

    menuTextUnderline(value: boolean) {
        const nodes = this.getSelectedOrAllNodes();

        for (const node of nodes) {
            node.textPart.menuTextUnderline(value);
            node.textPart.updateDims();
        }

        this.updateDims();
    }

    menuDirectionMode(directionMode: string) {
        this.directionMode = directionMode;
        this.alignMindMap();
    }

    resetFlagsAndInteraction() {
        if (this.interactionType === "nodeFocus") {
            const focusedNode = this.getOnlySelectedNode();
            if (focusedNode != null) {
                // тут скорее всего всегда будем выходить. resetFlagsAndInteraction вызывается если в selected есть что-то, чего уже нету. В случае nodeFocus это только одна нода
                focusedNode.textPart.unfocusText();
            }
        }

        this.interactionType = "";
        this.selected = [];

        const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");
        if (cursorTool.isMode("interaction") && canv.canvasController.isInSelected(this.parent)) {
            cursorTool.turnMode("");
        }
    }

    verifySelection() {
        for (let i = 0; i < this.selected.length; i++) {
            const node = this.findNode(this.selected[i]);
            if (node == null) {
                this.resetFlagsAndInteraction();
                return;
            }
        }
    }

    getNodesStatesFull() {
        const states: MindMapWidgetNodeState[] = [];

        for (let i = 0; i < this.nodes.length; i++) {
            states.push(this.nodes[i].getState("full"));
        }

        return states;
    }

    createNodes(nodes: MindMapWidgetNode[]) {
        for (let i = 0; i < nodes.length; i++) {
            const node = new MindMapWidgetNode(0, 0);
            node.loadState(nodes[i] as unknown as MindMapWidgetNodeState, "full");
            this.nodes.push(node);
        }
    }

    deleteNodes(nodes: MindMapWidgetNode[]) {
        for (let i = 0; i < nodes.length; i++) {
            this.removeNode(nodes[i].UID);
        }
    }

    getNodeBuildStatesWithTwin(
        state: MindMapTwinState,
        twinState: MindMapTwinState | undefined = undefined,
    ) {
        if (twinState === undefined) {
            state.action = this.branchBuildFixData.action;
            state.nodes = this.branchBuildFixData.nodes;
        } else {
            if (twinState.action === "delete") {
                state.action = "create";
                state.nodes = twinState.nodes.map((nodeState) => {
                    const node = this.findNode(nodeState.UID);

                    return node?.getState("full") ?? nodeState;
                });
            } else {
                state.action = "delete";
                state.nodes = twinState.nodes; // тут нельзя for бегать как в twinState["action"] == "delete" и собирать state, так как create event еще не сработал и их нету
            }
        }
    }

    getNodesStatesWithTwin(
        twinState: MindMapTwinState | undefined = undefined,
        nodeFixType: MindMapWidgetNodeFixState,
    ) {
        const states: Array<
            | MindMapWidgetNodeState
            | Pick<MindMapWidgetNodeState, "UID" | "startX" | "startY" | "color" | "textPart">
        > = [];

        if (twinState !== undefined) {
            const prevStates = twinState.nodes;

            for (let i = 0; i < prevStates.length; i++) {
                const node = this.findNode(prevStates[i].UID);
                if (node == null) continue; // это уже проверили в checkEvent
                states.push(node.getState(nodeFixType));
            }
        } else {
            const nodes = this.getSelectedOrAllNodes();
            for (let i = 0; i < nodes.length; i++) {
                states.push(nodes[i].getState(nodeFixType));
            }
        }

        return states;
    }

    loadNodesStates(nodes: MindMapWidgetNode[], nodeFixType: string) {
        for (let i = 0; i < nodes.length; i++) {
            const node = this.findNode(nodes[i].UID);
            if (node == null) continue; // это уже проверили в checkEvent
            node.loadState(
                nodes[i] as unknown as MindMapWidgetNodeState,
                nodeFixType as MindMapWidgetNodeFixState,
            );
        }
    }

    checkEvent(stateObj: any, fixType: string) {
        if (fixType === "nodeBuild") {
            if (stateObj.action === "create") {
                const UIDs = new Map();

                // проверяем, что их нету
                for (let i = 0; i < stateObj.nodes.length; i++) {
                    UIDs.set(stateObj.nodes[i].UID, 0);
                    const node = this.findNode(stateObj.nodes[i].UID);
                    if (node != null) {
                        return false;
                    }
                }

                // проверяем все parentNodeUID
                for (let i = 0; i < stateObj.nodes.length; i++) {
                    if (
                        !UIDs.has(stateObj.nodes[i].parentNodeUID) &&
                        this.findNode(stateObj.nodes[i].parentNodeUID) == null
                    ) {
                        return false;
                    }

                    // проверяем direction
                    if (this.findNode(stateObj.nodes[i].parentNodeUID) != null) {
                        const parentNode = this.findNode(stateObj.nodes[i].parentNodeUID);

                        // проверяем direction
                        if (
                            parentNode?.direction !== stateObj.nodes[i].direction &&
                            parentNode?.direction !== ""
                        ) {
                            return false;
                        }

                        if (parentNode) {
                            // проверяем, что после добавление узла не возникнет детей с одинаковым level - это вызывает exception у generateBeetwenKey в stopFly
                            const parentNodeChildren = this.getChildren(
                                parentNode,
                                stateObj.nodes[i].direction,
                            );
                            for (let k = 0; k < parentNodeChildren.length; k++) {
                                if (parentNodeChildren[k].level === stateObj.nodes[i].level) {
                                    return false;
                                }
                            }
                        }
                    }
                }
            } else {
                const UIDs = new Map();

                // проверяем, что они есть
                for (let i = 0; i < stateObj.nodes.length; i++) {
                    UIDs.set(stateObj.nodes[i].UID, 0);
                    const node = this.findNode(stateObj.nodes[i].UID);
                    if (node == null) {
                        return false;
                    }
                }

                // проверяю, что удаляются целые ветви. То-есть, что дети удаляемых нод тоже есть среди удаляемых нод
                for (let i = 0; i < stateObj.nodes.length; i++) {
                    const node = this.findNode(stateObj.nodes[i].UID); // уже проверили, что есть выше
                    if (node == null) continue;
                    const children = this.getChildren(node, node.direction); // именно node.direction, так как мы смотрим на текущих childs
                    for (let k = 0; k < children.length; k++) {
                        if (!UIDs.has(children[k].UID)) {
                            return false;
                        }
                    }
                }
            }
        } else if (fixType === "nodes") {
            // проверяем наличие всех нод
            for (let i = 0; i < stateObj.nodes.length; i++) {
                const node = this.findNode(stateObj.nodes[i].UID);
                if (node == null) {
                    return false;
                }
            }
        }

        return true;
    }

    getFixType(reason: string) {
        let fixType = "full";

        switch (reason) {
            case "resize":
                fixType = "empty"; // чтобы не отправлять full
                break;
            case "nodeBuild":
                fixType = "nodeBuild";
                break;
            case "move":
            case "rootMove":
            case "nodeFocus":
            case "wm_color":
            case "wm_textBold":
            case "wm_textStrike":
            case "wm_textBulleted":
            case "wm_textSize":
                fixType = "nodes";
                break;
        }

        return fixType;
    }

    getState<T extends MindMapWidgetNodeFixState>(
        fixType: T | string,
        twinState?: MindMapTwinState,
    ) {
        switch (fixType) {
            case "nodes":
                return {
                    nodes: this.getNodesStatesWithTwin(twinState, "props"),
                };
            case "nodeBuild": {
                // не сработает для rootNode, так как в checkEvent используется getChildren с указанием direction
                const state: MindMapTwinState = {
                    action: "",
                    nodes: [],
                };

                this.getNodeBuildStatesWithTwin(state, twinState);

                return state;
            }
            case "full":
                return {
                    UID: this.UID,
                    parentUID: this.parentUID,
                    entityType: "mindMapWidget",
                    nodes: this.getNodesStatesFull(), // startX, startY of root node определяет положение mindMap
                    directionMode: this.directionMode,
                };
        }
    }

    loadState(stateObj, fixType: string): void {
        switch (fixType) {
            case "nodes":
                this.loadNodesStates(stateObj.nodes, "props");
                this.alignMindMap();
                break;
            case "nodeBuild":
                if (stateObj.action === "create") {
                    this.createNodes(stateObj.nodes);
                } else {
                    this.deleteNodes(stateObj.nodes);
                }
                this.alignMindMap();
                this.verifySelection();

                break;
            case "full":
                this.UID = getStringProperty("UID", stateObj);
                this.parentUID = getStringProperty("parentUID", stateObj);

                this.nodes = [];
                this.createNodes(stateObj.nodes);

                this.directionMode = getStringProperty("directionMode", stateObj);
                this.alignMindMap();
                this.verifySelection();

                break;
        }

        this.updateDims();
    }

    constructorName: "mindMap" = "mindMap";
    startX = 0;
    startY = 0;
    width = 0;
    height = 0;

    interface: MindMapWidgetInterface;

    nodes: MindMapWidgetNode[] = [];
    directionMode = "horizontal";

    isLocked: boolean = false;
    selected: string[] = [];
    interactionType = "";
    flyNode: MindMapWidgetNode | null = null;
    flyData: MindMapFlyData | null = null;
    nodesSelectingData = {
        startX: 0,
        startY: 0,
        width: 0,
        height: 0,
        simulatedStartX: 0,
        simulatedStartY: 0,
        simulatedWidth: 0,
        simulatedHeight: 0,
    };

    branchBuildFixData: MindMapTwinState = {
        nodes: [],
        action: "",
    };
}
